/**
 * The five-stage `Report Status` pill (§A4; `04-officials-profiles.md` §A3).
 *
 * **One shared derived value, not two copies.** §A3 of the profiles spec says of this map:
 * *"the mockups duplicate this map verbatim between the roster and the Reporting page. In the
 * build it must be one shared derived value, not two copies."* So the label and the tooltip
 * are read from `REPORT_STAGE_LABELS` / `REPORT_STAGE_TOOLTIPS` and the tone from
 * `reportStagePillTone`, all in `@hockeytd/shared`, and nothing is restated here.
 *
 * `Owned By UM` (§A5, ruling R2′) sits **beside** the pill rather than inside it: the stage
 * says how far the report has got, the marker says whose it is, and collapsing the two would
 * make an umpire's pipeline unreadable.
 */

import {
  REPORT_STAGE_LABELS,
  REPORT_STAGE_TOOLTIPS,
  reportStagePillTone,
  type ReportStage,
} from "@hockeytd/shared";

/**
 * Which of the two treatments the design draws for this stage.
 *
 *  · `pill` — the generic `.statuspill`, in the tone `reportStagePillTone` gives the stage.
 *    This is what `official-profile-detail-v1.html` draws: one person's own screen, where the
 *    stage sits among other status pills and must read as one of them.
 *  · `chip` — `reporting-page-v1.html` and `officials-profiles-page-v1.html` draw the roster
 *    column as a five-step `.repchip` RAMP instead: `Blank` is a dashed hollow outline and
 *    each further stage is visually stronger, ending in a solid green for `TMS Published`,
 *    the one state the TD cannot change any more. Down a column of fifty rows the ramp is
 *    readable as a pipeline in a way five equal-weight tones are not, which is why the two
 *    mockups differ rather than one of them being stale.
 *
 * `pill` is the default so that every existing caller keeps the treatment its own mockup
 * draws; the roster panel asks for `chip` explicitly.
 */
export type ReportStatusVariant = "pill" | "chip";

export function ReportStatusPill({
  stage,
  variant = "pill",
}: {
  stage: ReportStage;
  variant?: ReportStatusVariant;
}): React.JSX.Element {
  if (variant === "chip") {
    return (
      <span
        className={`repchip repchip--s${String(stage)}`}
        title={REPORT_STAGE_TOOLTIPS[stage]}
        data-stage={String(stage)}
      >
        <i className="rdot" aria-hidden="true" />
        {REPORT_STAGE_LABELS[stage]}
      </span>
    );
  }

  return (
    <span
      className={`statuspill statuspill--${reportStagePillTone(stage)}`}
      title={REPORT_STAGE_TOOLTIPS[stage]}
      data-stage={String(stage)}
    >
      <span className="sdot" aria-hidden="true" />
      {REPORT_STAGE_LABELS[stage]}
    </span>
  );
}

/** §A5's marker on every umpire row — the report is the owning UM's to draft and to push. */
export function OwnedByUmMarker({
  ownerUmShortName,
}: {
  ownerUmShortName: string | null;
}): React.JSX.Element {
  return (
    <span
      className="ownedbyum"
      title={
        ownerUmShortName === null
          ? "Drafted, Owned And Pushed By The Owning Umpires Manager"
          : `Drafted, Owned And Pushed By ${ownerUmShortName}`
      }
    >
      Owned By UM
    </span>
  );
}

/**
 * §A4's pipeline key — the five chips separated by `→` arrows, in order.
 *
 * Built from `REPORT_STAGES`, so it cannot list four stages, or the five in the wrong order,
 * while the chips above it use the real map. The key draws the SAME `.repchip` ramp the
 * column above it draws, which is the whole point of a key: `reporting-page-v1.html` prints
 * `Blank → AI Drafted → TD Drafted → TMS Draft → TMS Published` in the exact chips the rows
 * use, so a reader matches a row to the key by shape rather than by reading both.
 */
export function PipelineKey({ stages }: { stages: readonly ReportStage[] }): React.JSX.Element {
  return (
    <p className="repkey" aria-label="Report status pipeline key">
      {stages.map((stage, index) => (
        <span key={stage} className="repkey__step">
          {index > 0 ? (
            <span className="arw" aria-hidden="true">
              →
            </span>
          ) : null}
          <span className={`repchip repchip--s${String(stage)}`}>
            <i className="rdot" aria-hidden="true" />
            {REPORT_STAGE_LABELS[stage]}
          </span>
        </span>
      ))}
    </p>
  );
}
